/**
 * What has been checked from outside the graph.
 *
 * Three panels and one writing surface, kept out of the node card
 * (`ui/NodeCard.tsx`) because they
 * answer a different question from everything else there: not "what does this node
 * say" but "what happened when somebody looked".
 *
 * The rule every string in this file follows: the label is never allowed to be
 * stronger than what produced it. A reading is what a search returned on a date. A
 * panel's agreement is agreement between passes, not correctness. A recorded
 * result is one result. Where a sentence could be read as the tool ruling on the
 * claim, it is rewritten until it cannot be.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { textTruncationNotes } from '../core/textRetention'
import {
  BEARING_LABEL,
  CORPUS_ONLY_CAVEAT,
  LENS_LABEL,
  OP_LABEL,
  READING_LABEL,
  auditLive,
  corpusMissNote,
  readingCaveat,
  readingNote,
  refutedBacking,
  sourceReach,
  verificationLive,
  type FieldSurvey,
  type PassageCite,
  type PriorArt,
  type ReasonNode,
  type Session,
  type Verification,
  type WebSource,
} from '../core/types'
import {
  CLOSENESS_WORD,
  STATUS_WORD,
  claimCount,
  quotedPress,
  truncationWord,
} from '../core/vocabulary'
import { calendarDay, calendarMinute, dayInputNoon } from '../core/dates'
import {
  PANEL_CHIP_LABEL,
  PANEL_VERDICT_LABEL,
  missingLenses,
  panelChip,
  panelSummary,
  sharpestDissent,
  tallyPanel,
  type PanelChip,
} from '../core/panel'
import {
  entryFor,
  latestOpOn,
  missingKeyNeed,
  resolveFor,
  useStore,
  type KeyNeed,
} from '../core/store'
import { PROVIDERS, fmtUsd, verifyOfferNote, type ModelEntry } from '../core/models'
import { SURVEY_NEED, SURVEY_PARTS, SURVEY_PART_LABEL, surveyLive } from '../core/survey'
import { projectVerbUsd } from '../engine/run'
import { judgedDisagreement } from '../core/judged'
import { liveOrigin, liveOriginName, liveOriginUrl } from '../core/sources'
import { canonicalUrl, hostOf } from '../core/url'
import { decodeEntities } from '../core/entities'
import { DEMO_SESSION_ID, loadOutcomeRecords } from '../core/persist'
import { isAuthoredDemoOutcome } from '../engine/demo'
import { calibrate, resultPointer, resultPointerLine } from '../core/calibration'
import {
  diffVerifications,
  evidenceRebaseQueue,
  evidenceStaleForSource,
  latestEvidenceChange,
  pendingEvidenceStaleForSource,
  verificationEvents,
  type EvidenceDelta,
} from '../core/rebase'
import { Disclosure, Range } from './components/Controls'
import { missingKeyLine, pressName, pressTail, useRouteReady } from './pressName'
import { Icon } from './components/Icon'
import { VerbPrice } from './VerbPrice'
import {
  recordOutcome,
  rederiveFromOutcome,
  runAudit,
  auditTargets,
  auditorFor,
  withdrawOutcome,
} from '../engine/reason'
import { auditBatch, auditBatchSentence, lastSettledAudit } from '../core/auditReview'
import { fmtRating, selfRatingsOf } from '../core/rewrite'
import { upNextShut } from '../core/upNext'
import { pressRethink, pressVerb } from './upNextPress'
import { useVoiceOn } from './useVoiceOn'
import { useFigureBasis } from './useFigureBasis'
import { StanceStrip } from './StanceStrip'

/** The reader's calendar day, and minute — see `core/dates.ts`. These printed the
 *  UTC day, so a check made at 07:20 in Tokyo read as made the day before. */
const fmtDay = calendarDay
const fmtMoment = calendarMinute

/** The chip tint per reading. Only a refutation gets the loud one. */
const READING_TINT: Record<Verification['reading'], string> = {
  supported: 'var(--sem-signal)',
  mixed: 'var(--sem-gap)',
  refuted: 'var(--sem-refuted)',
  'nothing-found': 'var(--sem-weak)',
  'not-checkable': 'var(--sem-weak)',
  unsourced: 'var(--sem-weak)',
}

const verificationKey = (v: Verification, index: number): string =>
  v.opId ?? `legacy:${v.checkedAt}:${index}`

const deltaSize = (delta: EvidenceDelta): number =>
  delta.added.length + delta.removed.length + delta.changed.length

function EvidenceDeltaRows({ label, delta }: { label: string; delta: EvidenceDelta }) {
  if (deltaSize(delta) === 0) return null
  return (
    <div className="evidence-delta__group">
      <b>{label}</b>
      <ul>
        {delta.added.map((row) => (
          <li key={`added:${row.key}`} data-delta="added">
            Added · {row.label}
          </li>
        ))}
        {delta.removed.map((row) => (
          <li key={`removed:${row.key}`} data-delta="absent">
            Absent from later check · {row.label}
          </li>
        ))}
        {delta.changed.map((row) => (
          <li key={`changed:${row.key}`} data-delta="changed">
            Changed · {row.label}
          </li>
        ))}
      </ul>
    </div>
  )
}

/** A typed, local diff. No model is asked what changed or what it means. */
function VerificationDifference({ before, after }: { before: Verification; after: Verification }) {
  const diff = diffVerifications(before, after)
  const provenance: string[] = []
  if (diff.claimChanged) provenance.push('checked wording')
  if (diff.scopeChanged) provenance.push('evidence scope')
  if (diff.partialChanged) provenance.push('partial-search state')
  if (diff.countsChanged) provenance.push('coverage counts')
  if (diff.coverageChanged) provenance.push('document retrieval coverage')
  if (diff.modelChanged) provenance.push('checking model')
  if (diff.summaryChanged) provenance.push('model summary')

  return (
    <section className="evidence-delta" aria-label="Changes from the preceding source check">
      <div className="evidence-delta__head">
        <span>Before · {fmtMoment(before.checkedAt)}</span>
        <Icon name="chevron" size={11} />
        <span>After · {fmtMoment(after.checkedAt)}</span>
      </div>
      {diff.readingChanged && (
        <p className="evidence-delta__reading">
          Reading · <del>{READING_LABEL[before.reading]}</del>{' '}
          <ins>{READING_LABEL[after.reading]}</ins>
        </p>
      )}
      <EvidenceDeltaRows label="Web sources" delta={diff.sources} />
      <EvidenceDeltaRows label="Document passages" delta={diff.corpus} />
      <EvidenceDeltaRows label="Prior art" delta={diff.priorArt} />
      <EvidenceDeltaRows label="Search queries" delta={diff.queries} />
      <EvidenceDeltaRows label="Search failures" delta={diff.failures} />
      <EvidenceDeltaRows label="Retraction notices" delta={diff.notices} />
      {provenance.length > 0 && <p className="gnote">Also changed: {provenance.join(' · ')}.</p>}
      {!diff.hasChanges && (
        <p className="gnote">No typed evidence or provenance field changed between these checks.</p>
      )}
      {diff.affectsReasoning ? (
        <p className="gnote gnote--warn">
          At least one typed evidence field changed. Pathways marks earlier dependent reasoning for
          another look; it does not call that reasoning wrong.
        </p>
      ) : diff.hasChanges ? (
        <p className="gnote gnote--quiet">
          The visible provenance changed, but the typed evidence record did not detach downstream
          reasoning.
        </p>
      ) : null}
    </section>
  )
}

function EvidenceTimeline({
  events,
  selected,
  onSelect,
}: {
  events: Verification[]
  selected: number
  onSelect: (index: number) => void
}) {
  if (events.length < 2) return null
  return (
    <details className="evidence-history">
      <summary>
        <span>Evidence time machine</span>
        <span>{events.length} dated checks retained</span>
      </summary>
      <p className="gnote">
        Pick a check to read the exact source record saved then. The canvas still uses the current
        record; this control does not rewrite history.
      </p>
      <ol className="evidence-history__events" aria-label="Dated source checks">
        {[...events]
          .map((event, index) => ({ event, index }))
          .reverse()
          .map(({ event, index }) => (
            <li key={verificationKey(event, index)}>
              <button
                type="button"
                className="evidence-event"
                aria-pressed={selected === index}
                onClick={() => onSelect(index)}
              >
                <i style={{ background: READING_TINT[event.reading] }} />
                <span>
                  <b>{READING_LABEL[event.reading]}</b>
                  <small>{event.model.name}</small>
                </span>
                <time dateTime={new Date(event.checkedAt).toISOString()}>
                  {fmtMoment(event.checkedAt)}
                </time>
                {index === events.length - 1 && <em>Current</em>}
              </button>
            </li>
          ))}
      </ol>
    </details>
  )
}

/**
 * What a changed check did to the reasoning that rests on it, and the one press
 * that rethinks it — in plain words.
 *
 * The study's reader got "EPISTEMIC REBASE · PREVIEW — Author new heads from the
 * changed record…" off one mixed reading, and a press that ran out of the panel
 * with its price clipped. Everything that block said is still here, in the
 * order a reader asks it: what changed ("This check changed what “X” rests
 * on"), how much was reasoned before it (branches, counted off the same queue
 * the pass takes), and whether to rethink the next one — the press, which wraps
 * so its price is always on screen. The queue itself is a fold, free to open:
 * arithmetic over dependency links, never a model call (D65).
 *
 * After a rethink, the earlier branch stays on the board as the version being
 * compared and the canvas recedes it (`priorVersionIds`), rather than standing
 * it level beside the new heads; the before/after sets are the comparison fold,
 * with the before side dimmed the same way.
 *
 * Under a pass in flight the press is still offered: it waits on the Up next
 * line naming the branch it named here, and at its turn runs only if that is
 * still the next branch, off this same check (`pressRethink`). It greys out
 * only where the line cannot take it, as the check beside it does.
 */
export function RebaseImpact({
  session,
  node,
  onCompare,
}: {
  session: Session
  node: ReasonNode
  onCompare: (eventIndex: number) => void
}) {
  const revealNode = useStore((s) => s.revealNode)
  const select = useStore((s) => s.select)
  const shut = useStore((s) => upNextShut(s, 'rebase'))
  // Its name, as the verbs are named: the one pass it runs, on reasoning's route.
  const ready = useRouteReady('rebase')
  const est = useStore((s) =>
    projectVerbUsd({ modelId: s.modelId, roleModels: s.roleModels }, 'move'),
  )
  // Its figure includes the voice's line while the voice is on (D295).
  useVoiceOn()
  useFigureBasis()
  const change = latestEvidenceChange(node)
  const affected = evidenceStaleForSource(session, node.id)
  const pending = pendingEvidenceStaleForSource(session, node.id)
  const queue = evidenceRebaseQueue(session, node.id)
  const queuedForPass = queue.filter((branch) => branch.action === 'rederive')
  const rebases = (change?.rebaseOpIds ?? (change?.rebaseOpId ? [change.rebaseOpId] : [])).flatMap(
    (opId) => {
      const row = node.evidenceRebases?.find((candidate) => candidate.opId === opId)
      return row ? [row] : []
    },
  )
  const rebase = rebases.at(-1)
  const survivingHeads = [...new Set(rebases.flatMap((row) => row.produced))].flatMap((id) => {
    const head = session.nodes[id]
    return head ? [{ id, head }] : []
  })
  const priorConclusions = [...new Set(rebases.flatMap((row) => row.affected))].flatMap((id) => {
    const prior = session.nodes[id]
    return prior ? [{ id, prior }] : []
  })

  if (!change || affected.length === 0) return null

  const openNode = (id: string) => {
    revealNode(id)
    select(id)
  }
  const branches = (n: number) => `${n} branch${n === 1 ? '' : 'es'}`
  const next = queuedForPass[0]
  // What stands on the earlier record, and what has been rethought since —
  // counted in branches, the unit the press works in, off the queue it takes.
  const lede = rebase
    ? `${branches(rebases.length)} ${rebases.length === 1 ? 'has' : 'have'} been rethought since${
        queue.length
          ? `; ${queue.length} more ${queue.length === 1 ? 'was' : 'were'} reasoned before it.`
          : '. The earlier version stays on the board, dimmed, as the one to compare against.'
      }`
    : `${branches(queue.length)} ${queue.length === 1 ? 'was' : 'were'} reasoned before it.`

  return (
    <section className="rebase-impact">
      {/* The sentence is the live part, not the section: the section holds the
          press's before → after figure, which moves with every landing, and a
          region around it re-read the whole block, press and all, each time the
          running estimate ticked. */}
      <p className="rebase-impact__lede" aria-live="polite">
        <b>This check changed what “{node.label}” rests on.</b> {lede}
        {next ? ' Rethink the next one?' : ''}
      </p>
      {next ? (
        <>
          <button
            type="button"
            className="btn btn--primary btn--wrap rebase-impact__go"
            // Shut by a full line, not disabled, so focus stays on it; the press
            // says why in the line's own sentence (`pressRethink`).
            aria-disabled={shut || undefined}
            // The face prints the running before → after pair; the name calls
            // the one figure this press adds an estimate (`pressName`).
            aria-label={pressName(
              quotedPress(OP_LABEL.rebase, next.root.label),
              pressTail({ ready, est }),
            )}
            onClick={() => pressRethink(node.id, { id: next.root.id, label: next.root.label })}
          >
            <Icon name="spark" size={13} />
            <span>{quotedPress(OP_LABEL.rebase, next.root.label)}</span>
            <VerbPrice verb="move" instead={ready ? undefined : STATUS_WORD.needsKey} />
          </button>
          <p className="gnote gnote--quiet">
            One pass from the unchanged claim and this dated record. The branch it replaces stays on
            the board, dimmed, as the version to compare against; nothing is deleted. Being reasoned
            before a check does not make a branch wrong.
          </p>
        </>
      ) : queue.length > 0 ? (
        <p className="gnote gnote--warn">
          {queue.length === 1 ? 'The branch left crosses' : 'The branches left cross'} a synthesis
          or another branch, so one pass from this claim cannot rebuild{' '}
          {queue.length === 1 ? 'it' : 'them'}. Open each to review it with all of its dependencies.
        </p>
      ) : null}

      {(rebase || survivingHeads.length > 0) && (
        <div className="rebase-impact__actions">
          {rebase && (
            <button type="button" className="linkish" onClick={() => onCompare(change.eventIndex)}>
              {change.eventIndex > 0 ? 'Compare source records' : 'Review source record'}
            </button>
          )}
          {survivingHeads.map(({ id, head }, index) => (
            <button type="button" className="linkish" key={id} onClick={() => openNode(id)}>
              Open head {index + 1}
              {head.label ? ` · ${head.label}` : ''}
            </button>
          ))}
        </div>
      )}

      {queue.length > 0 && (
        <details className="rebase-conclusions">
          <summary>
            {queue.length === 1 ? 'The branch' : `The ${queue.length} branches`} · {pending.length}{' '}
            claim{pending.length === 1 ? '' : 's'}
          </summary>
          <ul className="rebase-plan">
            {queue.map((branch) => (
              <li key={branch.root.id}>
                <button type="button" onClick={() => openNode(branch.root.id)}>
                  <span>{branch.root.label}</span>
                  <small>
                    {branch === next
                      ? 'Next to rethink'
                      : branch.action === 'rederive'
                        ? 'Rethink'
                        : 'Review by hand'}{' '}
                    · {branch.affectedIds.length} claim{branch.affectedIds.length === 1 ? '' : 's'}
                  </small>
                </button>
              </li>
            ))}
          </ul>
          <p className="gnote gnote--quiet">
            Counted from dependency links; no model call. Existing claims, syntheses and recorded
            results are not rewritten or deleted, and a recorded result stops the count below it.
          </p>
        </details>
      )}

      {rebase && (
        <details className="rebase-conclusions">
          <summary>
            Compare conclusions · {priorConclusions.length} before / {survivingHeads.length} after
          </summary>
          <p className="gnote">
            These are the exact retained sets; Pathways does not invent one-to-one matches between
            differently structured heads.
          </p>
          <p className="gnote gnote--warn">
            Why reconsidered ·{' '}
            {change.readingBefore
              ? `${READING_LABEL[change.readingBefore]} → ${READING_LABEL[change.readingAfter]}`
              : READING_LABEL[change.readingAfter]}
            {change.evidenceRecordIds.length
              ? ` · changed records ${change.evidenceRecordIds.join(', ')}`
              : ' · the typed source record changed'}
          </p>
          <span className="eyebrow">After · the rethought heads</span>
          <ul className="rebase-plan">
            {survivingHeads.map(({ id, head }) => (
              <li key={id}>
                <button type="button" onClick={() => openNode(id)}>
                  <span>{head.claim}</span>
                  <small>{head.label}</small>
                </button>
              </li>
            ))}
          </ul>
          <span className="eyebrow">Before · the earlier version, kept for comparison</span>
          <ul className="rebase-plan rebase-plan--prior">
            {priorConclusions.map(({ id, prior }) => (
              <li key={id}>
                <button type="button" onClick={() => openNode(id)}>
                  <span>{prior.claim}</span>
                  <small>{prior.label}</small>
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  )
}

/* ── the source check ─────────────────────────────────────────────────── */

/**
 * Which half of a panel to render. The node card splits its two passes the
 * same way: the PRESS sits right under the claim, as the first thing a card
 * offers, and the RECORD — what a search returned — sits with the other
 * records further down, so the verbs are not pushed below a source list.
 * Omitted, both halves render in their old order.
 */
export type PanelPart = 'press' | 'record'

export function VerificationPanel({
  session,
  node,
  part,
}: {
  session: Session
  node: ReasonNode
  part?: PanelPart
}) {
  // Under a pass in flight the press is still offered while Up next has room:
  // it joins the line behind that pass, as V does (D162), and greys out only
  // where the line cannot take it.
  const shut = useStore((s) => upNextShut(s, 'verify'))
  // Everything the press says — its name, its cell, its title and its note —
  // over the CHECK route it runs on, never the selection: the note described the
  // selection's search and asked for the selection's key while the pass refused
  // for, and the sheet asked for, the routed company's (invariant 4, D202).
  const checkReady = useStore((s) => resolveFor(s, 'check') !== null)
  const modelId = useStore((s) => s.modelId)
  const roleModels = useStore((s) => s.roleModels)
  const route = entryFor({ modelId, roleModels }, 'check')
  const missing = missingKeyNeed({ modelId, roleModels }, 'check')
  const checkEst = useStore((s) =>
    projectVerbUsd({ modelId: s.modelId, roleModels: s.roleModels }, 'verify'),
  )
  // Its figure includes the voice's line while the voice is on (D295).
  useVoiceOn()
  useFigureBasis()
  const noteId = useId()
  const v = node.verification
  const events = verificationEvents(node)
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [compareOpen, setCompareOpen] = useState(false)
  const latestIndex = events.length - 1
  const soughtIndex =
    selectedKey === null
      ? latestIndex
      : events.findIndex((event, index) => verificationKey(event, index) === selectedKey)
  const selectedIndex = soughtIndex >= 0 ? soughtIndex : latestIndex
  const shown = events[selectedIndex]
  const before = selectedIndex > 0 ? events[selectedIndex - 1] : undefined
  const selectedIsCurrent = shown !== undefined && selectedIndex === latestIndex
  const op = shown?.opId
    ? session.ops.find((row) => row.id === shown.opId)
    : selectedIsCurrent
      ? latestOpOn(session, node.id, 'verify')
      : undefined
  const stale = shown && !verificationLive({ ...node, verification: shown })

  useEffect(() => {
    setSelectedKey(null)
    setCompareOpen(false)
  }, [node.id, v?.opId, v?.checkedAt])

  const chooseEvent = (index: number) => {
    const event = events[index]
    if (!event) return
    setSelectedKey(index === latestIndex ? null : verificationKey(event, index))
    setCompareOpen(false)
  }

  const press = (
    <button
      className="action action--wide"
      style={{ '--act-tint': 'var(--sem-signal)' } as CSSProperties}
      disabled={node.parentId === null}
      // A full Up next line shuts it without taking it out of the tab order, as
      // it does the verbs (`aria-disabled`); V's press path says why.
      aria-disabled={shut || undefined}
      title={
        checkReady
          ? 'Searches the live web for what the record says about this claim'
          : missingKeyLine(PROVIDERS[missing.provider], missing.need)
      }
      aria-label={pressName(
        v ? 'Check again' : 'Check against sources',
        pressTail({ ready: checkReady, est: checkEst }),
      )}
      aria-describedby={noteId}
      aria-keyshortcuts="V"
      onClick={() => pressVerb('verify', node.id)}
    >
      <b style={{ width: '100%' }}>
        <Icon name="search" size={13} />
        {v ? 'Check again' : 'Check against sources'}
        <VerbPrice verb="verify" kbd="V" instead={checkReady ? undefined : STATUS_WORD.needsKey} />
      </b>
      <span id={noteId}>
        {checkReady
          ? verifyOfferNote(route)
          : `${missingKeyLine(PROVIDERS[missing.provider], missing.need)} This one really does search the web, so there is no demo version of it.`}
      </span>
    </button>
  )
  if (part === 'press') return press
  if (part === 'record' && !shown) return null

  return (
    // Marked with its node, so the reading on the status line and the check's
    // message in the feed can bring this record onto the screen
    // (`scrollToCheckRecord`, D335).
    <div className="group" data-check-node={node.id} style={{ gap: 'var(--s2)' }}>
      {/* Only once a check has actually run does this become its own section.
          Before that the sheet keeps the source check as the last ROW of ONE
          PASS — one call, priced like the verbs above it — and the fact that
          nothing has been consulted is already stated twice higher up, by the
          SOURCES cell reading a dash and by "From the world". A third copy
          under its own header made the unchecked state look like a finding. */}
      {v && <span className="eyebrow">Against the record</span>}

      {shown && (
        <>
          <div
            className="grow-chip"
            style={{ '--c-tint': READING_TINT[shown.reading] } as CSSProperties}
          >
            <i />
            <b>{READING_LABEL[shown.reading]}</b>
            <span>{fmtDay(shown.checkedAt)}</span>
          </div>
          <EvidenceTimeline events={events} selected={selectedIndex} onSelect={chooseEvent} />
          {!selectedIsCurrent && (
            <p className="gnote gnote--warn">
              You are reading a retained earlier check. Canvas colour and dependency notices still
              follow the current source record.
            </p>
          )}
          {!selectedIsCurrent && shown.claimAtCheck && (
            <blockquote className="evidence-claim">
              <span>{shown.labelAtCheck ?? 'Claim at check time'}</span>
              <p>{shown.claimAtCheck}</p>
            </blockquote>
          )}
          {/* How the kept sources sided, counted, on every reading that kept
              any — and the note under it, which then says what the counts come
              to rather than printing them again. */}
          {shown.sources.length > 0 && (
            <p className="gnote">
              <StanceStrip sources={shown.sources} />
            </p>
          )}
          {/* `readingNote`, not the table: two of its lines are false on a
              documents-only pass, where no search ran at all. */}
          <p className="gnote">{readingNote(shown, { besideStrip: shown.sources.length > 0 })}</p>
          {refutedBacking(shown) && <p className="gnote gnote--warn">{refutedBacking(shown)}</p>}

          {stale && (
            <p className="gnote gnote--warn">
              You rewrote this claim after the check ran, so what was searched for is not what the
              node now says.
            </p>
          )}

          {readingCaveat(shown) && <p className="gnote gnote--warn">{readingCaveat(shown)}</p>}

          {shown.summary && <p className="insp-reasoning">{shown.summary}</p>}
          {textTruncationNotes(shown).map((note) => (
            <p className="gnote" key={note}>
              {note}
            </p>
          ))}

          {shown.sources.length > 0 && <SourceList sources={shown.sources} v={shown} />}
          {shown.corpus && shown.corpus.cites.length > 0 && (
            <PassageList cites={shown.corpus.cites} />
          )}
          {shown.priorArt.length > 0 && (
            <PriorArtBlock art={shown.priorArt} node={node} verification={shown} />
          )}

          <CheckProvenance v={shown} />

          {(shown.queries.length > 0 || op?.thinking) && (
            <Disclosure
              label="How this was checked"
              meta={
                shown.queries.length
                  ? `${shown.queries.length} quer${shown.queries.length === 1 ? 'y' : 'ies'}`
                  : undefined
              }
              hint={
                op?.thinking
                  ? "The model's reasoning on the pass that ran this check"
                  : 'The recorded queries for this source check'
              }
            >
              {shown.queries.length > 0 && (
                <p data-lead>It searched for: {shown.queries.map((q) => `“${q}”`).join(', ')}.</p>
              )}
              {op?.thinking?.split('\n\n').map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </Disclosure>
          )}
          {before && (
            <>
              <button
                type="button"
                className="linkish evidence-compare"
                aria-expanded={compareOpen}
                onClick={() => setCompareOpen((open) => !open)}
              >
                {compareOpen ? 'Hide changes' : 'Compare with the preceding check'}
              </button>
              {compareOpen && <VerificationDifference before={before} after={shown} />}
            </>
          )}
          {v && (
            <RebaseImpact
              session={session}
              node={node}
              onCompare={(eventIndex) => {
                const event = events[eventIndex]
                if (!event) return
                setSelectedKey(
                  eventIndex === latestIndex ? null : verificationKey(event, eventIndex),
                )
                setCompareOpen(eventIndex > 0)
              }}
            />
          )}
        </>
      )}

      {part !== 'record' && press}
    </div>
  )
}

/**
 * The judge's second reading, printed ONLY where it disagrees with the model's
 * stance and is sure enough to be worth reading (`judgedDisagreement`, D366).
 * Agreement is the ordinary case and would be noise on every row; the line says who
 * read it, what they read, and how sure — and never which of the two is right. It
 * changed no reading.
 */
function JudgedLine({ s, v }: { s: WebSource; v: Verification }) {
  const judged = judgedDisagreement(s)
  if (!judged) return null
  return (
    <span className="srcs-meta srcs-meta--warn">
      {judged.model.name} read this quote as{' '}
      {judged.stance === 'neither' ? 'bearing on something else' : 'supporting the claim'} (
      {judged.probabilities[judged.stance].toFixed(2)}), not as {v.model.name} did. The reading
      above stands on {v.model.name}’s stance.
    </span>
  )
}

/**
 * The sources, with exactly what was reached into each one.
 *
 * Two things here are per-PASS rather than per-source. "Read in full" is gated on
 * `quotesCheckable` and never on `via` alone, because a page opened by a model
 * whose search does not return page text was opened and not read. And when NO quote
 * on the pass could be checked, that is said once for the pass instead of six times
 * for six sources — six identical caveats read as six separate problems.
 */
function SourceList({ sources, v }: { sources: WebSource[]; v: Verification }) {
  const passLevel = v.quotesCheckable === false && sources.some((s) => !!s.quote)
  return (
    <>
      <ul className="srcs">
        {sources.map((s, i) => (
          <li key={i} data-stance={s.stance}>
            <a href={s.url} target="_blank" rel="noreferrer noopener">
              {/* A title this provider never supplied stands in as the URL; showing
                  the host twice is noise, so it collapses to one. Decoded here as
                  well as at collection, for titles stored before that. */}
              {s.title === s.url ? hostOf(s.url) : decodeEntities(s.title)}
            </a>
            <span className="srcs-meta">
              {hostOf(s.url)} · {sourceReach(s, v)}
              {s.stance === 'disputes'
                ? ' · disputes this'
                : s.stance === 'supports'
                  ? ' · supports this'
                  : ''}
            </span>
            {s.quote && (
              // Quotation marks assert a verbatim reading, so they appear only when
              // the quote was actually checked against the page's own text. A
              // snippet, a PDF that arrived as base64, or a provider that does not
              // return page text at all gets the unquoted form. Character
              // references are decoded for the eye and nowhere else: the stored
              // quote is what `quoteFound` was computed over, so it stays as it came.
              <p className="srcs-quote" data-checked={s.quoteFound === true || undefined}>
                {s.quoteFound === true ? `“${decodeEntities(s.quote)}”` : decodeEntities(s.quote)}
              </p>
            )}
            {!passLevel && s.quote && s.quoteFound === undefined && (
              <span className="srcs-meta">The quote could not be checked against the page.</span>
            )}
            {s.quote && s.quoteFound === false && (
              <span className="srcs-meta srcs-meta--warn">
                This wording was not found on the page.
              </span>
            )}
            <JudgedLine s={s} v={v} />
            {textTruncationNotes(s).map((note) => (
              <p className="srcs-meta srcs-meta--warn" key={note}>
                {note}
              </p>
            ))}
          </li>
        ))}
      </ul>
      {passLevel && (
        <p className="gnote gnote--quiet">
          {v.model.name} does not return the text of the pages it opens, so no quote on this check
          could be checked against its page.
        </p>
      )}
      {v.judgeNote && <p className="gnote gnote--quiet">{v.judgeNote}</p>}
    </>
  )
}

/**
 * A retraction needs no branch here, and adding one would be the defect.
 *
 * `liveSources.ts` writes ' — RETRACTED' into the doc TITLE before the record is
 * ever ranked, so `docTitle` on a cite from a withdrawn work already carries it —
 * in this list, in both exports, and in the passage table the model itself was
 * given. A second writer that appended the mark here would be a second rule about
 * how a retraction is shown, and D13's finding is that two copies of one rule
 * diverge. The structural `notice` field is what the coach reads; this surface
 * reads the title it was given.
 *
 * WHERE THE PASSAGE CAME FROM DOES need a branch, and it is per cite rather than
 * per list. "your own source" was printed over every row here, which was true
 * until the research rows went live and false afterwards for any passage a check
 * fetched — the loudest version of the same erasure D27's merge made in the
 * prompt. `liveOrigin` is the one derivation, off the stored `docId`; the user's
 * rows read exactly as they always did.
 *
 * AND SO DOES THE LINK. Saying a passage came from Europe PMC while giving a
 * reader no way to open it leaves them exactly where a citation is supposed to
 * stop leaving them, so the title becomes the door wherever the id carries one —
 * `liveOriginUrl`, the same derivation off the same stored field, in the same
 * `target`/`rel` shape `SourceList` above uses. A row with no door keeps the
 * plain `<b>`, which is every one of the user's own documents (there is nothing
 * to open) and the two fetched shapes that carry an identifier and no address.
 * `.srcs a` and `.srcs b` are styled identically, so the list does not go ragged
 * where the two mix.
 *
 * EXPORTED for the suite — `takeCueAction` in `Coach.tsx`'s precedent. The three
 * derivations below run on a STORED docId, which is third-party text, and a throw
 * out of one of them takes the whole pane down rather than one row; a test
 * that re-implemented the row instead of rendering it would not catch that.
 */
export function PassageList({ cites }: { cites: PassageCite[] }) {
  return (
    <ul className="srcs">
      {cites.map((c, i) => {
        const origin = liveOrigin(c.docId)
        // The row's own name where the id names one; `doi:` names no row, so that
        // cite says it was fetched and stops rather than picking a service.
        const from = origin ? liveOriginName(origin) : null
        const url = liveOriginUrl(c.docId)
        return (
          <li key={i}>
            {url ? (
              <a href={url} target="_blank" rel="noreferrer noopener">
                {c.docTitle}
              </a>
            ) : (
              <b>{c.docTitle}</b>
            )}
            <span className="srcs-meta">
              {origin ? `${from ? `${from} · ` : ''}fetched during this check` : 'your own source'}{' '}
              · passage {c.passage}
            </span>
            <p className="srcs-quote" data-checked>
              “{c.quote}”
            </p>
            {textTruncationNotes(c).map((note) => (
              <p className="srcs-meta srcs-meta--warn" key={note}>
                {note}
              </p>
            ))}
          </li>
        )
      })}
    </ul>
  )
}

function PriorArtBlock({
  art,
  node,
  verification,
}: {
  art: PriorArt[]
  node: ReasonNode
  verification: Verification
}) {
  // A reported result's matched study is where the result comes from, not
  // somebody saying it first — the claim is not penalised for citing its own
  // paper, so no novelty sentence rides under it (`core/priorWork.ts`).
  const reported = node.kind === 'evidence'
  const rated = selfRatingsOf(node)
  return (
    <div className="group" style={{ gap: 'var(--s1)' }}>
      <span className="eyebrow">
        {reported
          ? 'The source the check found for this result'
          : 'Someone may have said this already'}
      </span>
      <ul className="bullets" style={{ '--bullet-tint': 'var(--sem-gap)' } as CSSProperties}>
        {art.map((a, i) => (
          <li key={i}>
            {a.covers}{' '}
            <span style={{ color: 'var(--text-3)' }}>({CLOSENESS_WORD[a.closeness]})</span>
          </li>
        ))}
      </ul>
      {/* A rewritten claim has no "before": its stored novelty rated the words
          the reader replaced (`selfRatingsOf`, as the stat grid reads it). */}
      {reported ? null : verification.measuredNovelty !== undefined ? (
        rated ? (
          <p className="gnote">
            The model rated this {fmtRating(rated.novelty)} for novelty before searching, and{' '}
            {fmtRating(verification.measuredNovelty)} after seeing the work above. Both numbers are
            its own judgement; the second one had something to look at.
            {verification.noveltyNote ? ` ${verification.noveltyNote}` : ''}
          </p>
        ) : (
          <p className="gnote">
            The model rated this {fmtRating(verification.measuredNovelty)} for novelty after seeing
            the work above — its own judgement, with something to look at. Its rating before
            searching was of the wording you replaced.
            {verification.noveltyNote ? ` ${verification.noveltyNote}` : ''}
          </p>
        )
      ) : rated ? (
        <p className="gnote">
          The novelty rating on this node is unchanged — nothing found here was close enough to move
          it.
        </p>
      ) : (
        <p className="gnote">Nothing found here was close enough to re-rate its novelty.</p>
      )}
    </div>
  )
}

/** The arithmetic of the check, in one line. This is what makes an absence
 *  auditable: "nothing found" only means something next to what was looked for.
 *  Exported for its test, as `PassageList` is. */
export function CheckProvenance({ v }: { v: Verification }) {
  const bits: string[] = []
  if (v.queries.length) {
    bits.push(`${v.queries.length} search${v.queries.length === 1 ? '' : 'es'}`)
  }
  // Only when something searched. On a documents-only pass "0 results came back"
  // describes a search that never ran.
  if (v.scope !== 'corpus') {
    bits.push(`${v.seen} result${v.seen === 1 ? '' : 's'} came back`)
  }
  if (v.dropped > 0) {
    // "Them" is the results counted above, and a documents-only pass counts none:
    // a web address named on one was dropped because nothing searched.
    bits.push(
      v.scope === 'corpus'
        ? `${v.dropped} source${v.dropped === 1 ? '' : 's'} it named ${v.dropped === 1 ? 'was' : 'were'} dropped, since no search ran to return ${v.dropped === 1 ? 'it' : 'them'}`
        : `${v.dropped} source${v.dropped === 1 ? '' : 's'} it named ${v.dropped === 1 ? 'was' : 'were'} not among them and ${v.dropped === 1 ? 'was' : 'were'} dropped`,
    )
  }
  if (v.corpus) {
    // TWO clauses rather than one, since the research rows went live. The passage
    // pool is the user's documents AND whatever those rows returned for this
    // claim, so "N passages from your M documents" would credit their corpus with
    // records they never added — and with no documents at all it read "from your 0
    // documents". Each clause now says only what it can defend; which passage came
    // from where is on the passage itself, under its own title.
    bits.push(`${v.corpus.offered} passage${v.corpus.offered === 1 ? '' : 's'} offered`)
    // Not beside the miss below, which says the same count and says what came of it.
    if (v.corpus.docsAtCheck > 0 && !v.corpusSearched) {
      bits.push(
        `your ${v.corpus.docsAtCheck} document${v.corpus.docsAtCheck === 1 ? '' : 's'} searched`,
      )
    }
    if (v.corpus.dropped > 0) {
      bits.push(
        `${v.corpus.dropped} passage number${v.corpus.dropped === 1 ? '' : 's'} did not resolve`,
      )
    }
  }
  // The miss, which used to be silent: the documents were searched and none of
  // their passages reached the check. Without it a check that found nothing in two
  // documents read exactly like a check with none to search.
  const miss = corpusMissNote(v)
  if (miss) bits.push(miss)
  return (
    <>
      <p className="gnote gnote--quiet">{bits.join(' · ')}.</p>
      {v.partial && (
        <p className="gnote gnote--warn">
          The search was still running when this pass reached its limit. Read it as a partial look
          rather than a finished check.
        </p>
      )}
      {v.failures?.map((f, i) => (
        <p key={i} className="gnote gnote--warn">
          {f}
        </p>
      ))}
      {v.scope === 'corpus' && <p className="gnote gnote--quiet">{CORPUS_ONLY_CAVEAT}</p>}
    </>
  )
}

/* ── the survey of the field ──────────────────────────────────────────── */

/**
 * The seed's survey, and the press that runs one.
 *
 * The file's rule, one step further than a check needs it: a survey has no
 * reading at all, so nothing on this panel may read as a verdict on the question.
 * No chip, no hue, no stance on a source — what one search returned, on a date,
 * by which model, and what it set aside. The press is priced by the check's own
 * arithmetic, because it is the check's pass in every respect a reader pays for.
 */
export function SurveyPanel({ node, part }: { node: ReasonNode; part?: PanelPart }) {
  // The key's own path: V on the seed is `pressVerb('verify', seed)`, which runs
  // the survey now or queues it behind the pass in flight (D143, D162). The
  // button is that press, and greys out only where the line cannot take it.
  const shut = useStore((s) => upNextShut(s, 'verify'))
  const ready = useStore((s) => resolveFor(s, 'check') !== null)
  const route = useStore((s) => entryFor(s, 'check'))
  const modelId = useStore((s) => s.modelId)
  const roleModels = useStore((s) => s.roleModels)
  // The check's own arithmetic, over the CHECK route — what `VerbPrice` shows
  // for a check, and so what this pass is estimated at before the press.
  const est = projectVerbUsd({ modelId, roleModels }, 'verify')
  // …with the voice's line while the voice is on (D295).
  useVoiceOn()
  useFigureBasis()
  return (
    <SurveyCard
      node={node}
      press={{ shut, ready, route, est }}
      onPress={() => pressVerb('verify', node.id)}
      part={part}
    />
  )
}

/** What the survey's key card would say needed the key (`SURVEY_NEED`), on the
 *  check route's model — its note says it before the press. */
const surveyNeed = (route: ModelEntry): KeyNeed => ({ what: SURVEY_NEED, on: route.name })

/**
 * The panel with the store read above it. Exported for its test, the way
 * `CheckProvenance` is: a server render only ever sees the store's initial
 * state, so the routed and keyless cases can be rendered only through props.
 */
export function SurveyCard({
  node,
  press,
  onPress,
  part,
}: {
  node: ReasonNode
  /** `shut`: a pass is in flight and the Up next line cannot take this press
   *  (`upNextShut`). A pass in flight alone does not grey it out — the press
   *  waits on the line. */
  press: { shut: boolean; ready: boolean; route: ModelEntry; est: number | null }
  onPress: () => void
  /** See `PanelPart` — the seed's card splits the press from the record the
   *  way a claim's card splits its source check. */
  part?: PanelPart
}) {
  const { shut, ready, route, est } = press
  const survey = node.survey
  const earlier = node.surveyHistory ?? []
  const noteId = useId()

  const button = (
    <button
      className="action action--wide action--priced"
      style={{ '--act-tint': 'var(--sem-signal)' } as CSSProperties}
      // Shut as the check is: focusable, and V's press path says why.
      aria-disabled={shut || undefined}
      title={
        ready
          ? 'Searches the live web for what has been found, tried and disputed on this question'
          : missingKeyLine(PROVIDERS[route.provider], surveyNeed(route))
      }
      // As the check's press is named: the word, then what it spends — keyless,
      // the status word, which its cell now prints too (`pressName`).
      aria-label={pressName(
        survey ? 'Survey again' : 'Survey the field',
        pressTail({ ready, est }),
      )}
      aria-describedby={noteId}
      aria-keyshortcuts="V"
      onClick={onPress}
    >
      <b style={{ width: '100%' }}>
        <Icon name="search" size={13} />
        {/* No key on the check route, no figure: the name says so, and so does
            the cell (`instead`), rather than pricing a pass that will not run. */}
        {`${survey ? 'Survey again' : 'Survey the field'}${ready && est !== null ? ` · est. ${fmtUsd(est)}` : ''}`}
        <VerbPrice verb="verify" kbd="V" instead={ready ? undefined : STATUS_WORD.needsKey} />
      </b>
      <span id={noteId}>
        {!ready
          ? `${missingKeyLine(PROVIDERS[route.provider], surveyNeed(route))} This one really does search the web, so there is no demo version of it.`
          : route.search === 'none'
            ? `${route.name} has no web search, so there is nothing for a survey to read. Route source checks to a model that can search.`
            : 'The main bodies of evidence on this question and the kind of study behind each, landmark results, what has been tried, where results disagree and what has not been tested — from what the search returns. Priced like a source check.'}
      </span>
    </button>
  )
  if (part === 'press') return button
  if (part === 'record' && !survey) return null

  return (
    // The seed's record, marked as a claim's check record is (D335).
    <div className="group" data-check-node={node.id} style={{ gap: 'var(--s2)' }}>
      {survey && (
        <>
          <span className="eyebrow">Survey of the field</span>
          <p className="gnote">
            What one search returned about this question on {fmtDay(survey.surveyedAt)}. A survey of
            those results — not a verdict on the question, and not the whole literature.
          </p>
          {!surveyLive(node) && (
            <p className="gnote gnote--warn">
              The question was reworded after this survey ran, so what was searched for is not what
              the seed now says.
            </p>
          )}
          {survey.summary && <p className="insp-reasoning">{survey.summary}</p>}
          {textTruncationNotes(survey).map((note) => (
            <p className="gnote" key={note}>
              {note}
            </p>
          ))}
          <SurveyRows survey={survey} />
          {survey.sources.length > 0 && <SurveySources survey={survey} />}
          <SurveyProvenance survey={survey} />
          {survey.queries.length > 0 && (
            <Disclosure
              label="How this was searched"
              meta={`${survey.queries.length} quer${survey.queries.length === 1 ? 'y' : 'ies'}`}
              hint="The recorded queries for this survey"
            >
              <p data-lead>It searched for: {survey.queries.map((q) => `“${q}”`).join(', ')}.</p>
            </Disclosure>
          )}
          {earlier.length > 0 && (
            <Disclosure
              label="Earlier surveys"
              meta={String(earlier.length)}
              hint="Surveys this one replaced, kept because each was a search that happened"
            >
              {[...earlier].reverse().map((e) => (
                <p key={e.opId}>
                  {fmtDay(e.surveyedAt)} · {e.model.name} · {e.sources.length} returned source
                  {e.sources.length === 1 ? '' : 's'}
                  {e.summary ? ` — ${e.summary}` : ''}
                </p>
              ))}
            </Disclosure>
          )}
        </>
      )}

      {part !== 'record' && button}
    </div>
  )
}

/** The rows, under the five questions a survey answers, each naming the numbered
 *  sources below it rests on. An `untested` row names none: an absence has no page. */
function SurveyRows({ survey }: { survey: FieldSurvey }) {
  const numbered = new Map(survey.sources.map((s, i) => [canonicalUrl(s.url), i + 1]))
  return (
    <>
      {SURVEY_PARTS.map((part) => {
        const rows = survey.rows.filter((row) => row.part === part)
        if (!rows.length) return null
        return (
          <div className="group" style={{ gap: 'var(--s1)' }} key={part}>
            <span className="eyebrow">{SURVEY_PART_LABEL[part]}</span>
            <ul className="bullets" style={{ '--bullet-tint': 'var(--sem-weak)' } as CSSProperties}>
              {rows.map((row, i) => {
                const refs = row.refs
                  .map((ref) => numbered.get(ref))
                  .filter((n): n is number => n !== undefined)
                return (
                  <li key={i}>
                    {row.text}
                    {(row.design || refs.length > 0) && (
                      <span style={{ color: 'var(--text-3)' }}>
                        {row.design ? ` · ${row.design}` : ''}
                        {refs.length ? ` · [${refs.join(', ')}]` : ''}
                      </span>
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        )
      })}
    </>
  )
}

/** The returned sources, numbered as the rows cite them, titles as links. The
 *  title is the search's own, never the model's; decoded for the eye only. */
function SurveySources({ survey }: { survey: FieldSurvey }) {
  return (
    <div className="group" style={{ gap: 'var(--s1)' }}>
      <span className="eyebrow">What the search returned that it rests on</span>
      <ol className="srcs">
        {survey.sources.map((s, i) => (
          <li key={s.url}>
            <a href={s.url} target="_blank" rel="noreferrer noopener">
              [{i + 1}] {s.title === s.url ? hostOf(s.url) : decodeEntities(s.title)}
            </a>
            <span className="srcs-meta">
              {hostOf(s.url)} · {s.via === 'fetch' ? 'page opened' : 'search result only'}
            </span>
          </li>
        ))}
      </ol>
    </div>
  )
}

/**
 * The arithmetic of the survey, in one line, and what it set aside — the check's
 * `CheckProvenance`, for the same reason: an absence in a survey only means
 * something next to what was looked for. Exported for its test.
 */
export function SurveyProvenance({ survey }: { survey: FieldSurvey }) {
  const bits = [
    `Surveyed ${fmtDay(survey.surveyedAt)} by ${survey.model.name}`,
    `${survey.searches} search${survey.searches === 1 ? '' : 'es'}`,
    `${survey.seen} result${survey.seen === 1 ? '' : 's'} came back`,
  ]
  if (survey.dropped > 0) {
    bits.push(
      `${survey.dropped} source${survey.dropped === 1 ? '' : 's'} it named ${survey.dropped === 1 ? 'was' : 'were'} not among them and ${survey.dropped === 1 ? 'was' : 'were'} set aside`,
    )
  }
  if (survey.rowsSetAside > 0) {
    bits.push(
      `${survey.rowsSetAside} row${survey.rowsSetAside === 1 ? '' : 's'} set aside with no returned source behind ${survey.rowsSetAside === 1 ? 'it' : 'them'}`,
    )
  }
  return (
    <>
      <p className="gnote gnote--quiet">{bits.join(' · ')}.</p>
      {survey.sources.length === 0 && (
        <p className="gnote gnote--warn">
          No source this survey named was among the results the search returned, so its prose was
          discarded unread.
        </p>
      )}
      {survey.partial && (
        <p className="gnote gnote--warn">
          The search was still running when this pass reached its limit. Read it as a partial look.
        </p>
      )}
      {survey.failures?.map((f, i) => (
        <p key={i} className="gnote gnote--warn">
          {f}
        </p>
      ))}
    </>
  )
}

/* ── the panel ────────────────────────────────────────────────────────── */

/** The chip tint per state. `--sem-weak` on the third for the reason
 *  `nothing-found` and `inconclusive` take it above: the pass ran and there was
 *  nothing there to conclude from, which is neither a finding nor its opposite. */
const PANEL_CHIP_TINT: Record<PanelChip, string> = {
  disagreed: 'var(--sem-gap)',
  agreed: 'var(--sem-signal)',
  'too-few-decided': 'var(--sem-weak)',
}

export function PanelBlock({
  session,
  node,
  part,
}: {
  session: Session
  node: ReasonNode
  /** The press alone — the panel and the cross-examination, which the composer's
   *  Investigate mode carries (D347) — or the record alone, which the card keeps.
   *  Both, unsplit, where neither is named. */
  part?: PanelPart
}) {
  // P's own path: under a pass in flight the panel joins the Up next line behind
  // it (D162) and greys out only where the line cannot take it.
  const shut = useStore((s) => upNextShut(s, 'panel'))
  // What its name says, over the PANEL route it runs on (`pressName`): the key
  // chip and the note were all a screen reader heard of it, "Ask three lenses
  // separately P Reasons…", and the figure only ever showed on hover. Its cell,
  // title and note read the same route — they read the selection's key once,
  // and named the selection's company, for a pass routed to another.
  const panelReady = useRouteReady('panel')
  const modelId = useStore((s) => s.modelId)
  const roleModels = useStore((s) => s.roleModels)
  const missing = missingKeyNeed({ modelId, roleModels }, 'panel')
  const panelEst = useStore((s) =>
    projectVerbUsd({ modelId: s.modelId, roleModels: s.roleModels }, 'panel'),
  )
  // Its figure includes the voice's line while the voice is on (D295).
  useVoiceOn()
  useFigureBasis()
  const panelNote = useId()
  const demo = session.id === DEMO_SESSION_ID
  const p = node.panel
  const op = latestOpOn(session, node.id, 'panel')
  const t = p ? tallyPanel(p) : null
  const dissent = p ? sharpestDissent(p) : null
  // Three states, not two: "no split" is not agreement on a panel that came back
  // without a pair to compare. The words and the rule live together in `panel.ts`.
  const chip = p ? panelChip(p) : null

  const press = (
    <>
      <button
        className="action action--wide"
        style={{ '--act-tint': 'var(--sem-gap)' } as CSSProperties}
        disabled={node.parentId === null}
        // Shut by a full Up next line as the check is: focusable, and P's press
        // path says why.
        aria-disabled={shut || undefined}
        title={
          panelReady
            ? 'Three separate passes, each under a different lens, none seeing the others'
            : missingKeyLine(PROVIDERS[missing.provider], missing.need)
        }
        aria-label={pressName(
          p ? 'Run the panel again' : 'Ask three lenses separately',
          pressTail({ ready: panelReady, est: panelEst }),
        )}
        aria-describedby={panelNote}
        aria-keyshortcuts="P"
        onClick={() => pressVerb('panel', node.id)}
      >
        <b style={{ width: '100%' }}>
          <Icon name="invert" size={13} />
          {p ? 'Run the panel again' : 'Ask three lenses separately'}
          <VerbPrice verb="panel" kbd="P" instead={panelReady ? undefined : STATUS_WORD.needsKey} />
        </b>
        <span id={panelNote}>
          {panelReady
            ? 'Reasons this one claim three times under three disciplines, blind to each other, then counts where they landed. Three passes, so roughly three times the cost of one Deepen.'
            : `${missingKeyLine(PROVIDERS[missing.provider], missing.need)} Three independent passes cannot be pre-authored honestly.`}
        </span>
      </button>

      <AuditButton node={node} />
    </>
  )
  if (part === 'press') return press
  if (part === 'record' && !p) return null

  return (
    <div className="group" style={{ gap: 'var(--s2)' }}>
      {/* Same rule as the source check: under SEVERAL PASSES the sheet lists
          two rows, not two sub-sections. The header earns its place once there
          is a result under it to head. */}
      {p && <span className="eyebrow">Read again, separately</span>}

      {!p && <p className="gnote">This claim has been reasoned once, under one lens.</p>}

      {p && t && chip && (
        <>
          <div className="grow-chip" style={{ '--c-tint': PANEL_CHIP_TINT[chip] } as CSSProperties}>
            <i />
            <b>{PANEL_CHIP_LABEL[chip]}</b>
            <span>{fmtDay(p.checkedAt)}</span>
          </div>
          <p className="gnote">
            {p.model ? `Read again with ${p.model.name}. ` : ''}
            {panelSummary(p)}
          </p>

          <ul className="votes">
            {p.votes.map((vote, i) => (
              <li key={i}>
                <div className="votes-head">
                  <b>{LENS_LABEL[vote.lens]}</b>
                  <span data-v={vote.verdict}>{PANEL_VERDICT_LABEL[vote.verdict]}</span>
                  {/* Per pass, never averaged: an average of three verdicts is a
                      fourth opinion wearing the costume of a measurement. */}
                  <span className="num">{vote.confidence.toFixed(2)}</span>
                </div>
                <p>{vote.because}</p>
              </li>
            ))}
            {/* By name, never by count: `votes` holds the seats that RETURNED, so
                slicing the lens list by how many arrived names a lens that
                answered whenever an early seat failed. */}
            {missingLenses(p).map((lens) => (
              <li key={`missing-${lens}`} data-missing>
                <div className="votes-head">
                  <b>{LENS_LABEL[lens]}</b>
                  <span>did not return</span>
                </div>
              </li>
            ))}
          </ul>

          {dissent && (
            <p className="gnote">
              The furthest dissent came from the {LENS_LABEL[dissent.lens].toLowerCase()} lens.
            </p>
          )}

          {demo && (
            <p className="gnote gnote--quiet">
              This panel is part of the demo exploration. Nobody ran it — the verdicts were written
              by hand to show what the display looks like.
            </p>
          )}

          {op?.thinking && (
            <Disclosure
              label="What each pass was thinking"
              meta={`${p.votes.length} of ${p.lenses.length}`}
              hint="One trace per seat, in the order the seats ran"
            >
              {op.thinking.split('\n\n').map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </Disclosure>
          )}
        </>
      )}

      {part !== 'record' && press}
      {part === undefined && <AuditRecord node={node} />}
    </div>
  )
}

/* ── the cross-provider audit ─────────────────────────────────────────── */

/**
 * The panel's own limit says three passes of one model converging is one model
 * converging. This is the button that escapes it — and when it cannot, it says
 * so and stays disabled rather than quietly running in the same house, which
 * would be the panel again wearing a stronger word.
 */
export function AuditBlock({ node }: { node: ReasonNode }) {
  const a = node.audit
  if (!a) return null
  const live = auditLive(node)
  return (
    <div className="group">
      <div className="group-head">
        <b style={{ color: a.verdict === 'objection' ? 'var(--sem-tension)' : 'var(--text-1)' }}>
          {a.verdict === 'objection'
            ? `Proposed criticism${a.scope ? ` of ${a.scope}` : ' (historical scope unspecified)'}`
            : a.verdict === 'insufficient-context'
              ? 'Review needs more context'
              : 'No specific objection found'}
        </b>
        <span className="srcs-meta">{a.model.name}</span>
      </div>
      {a.note && <p>{a.note}</p>}
      <p className="gnote gnote--quiet">
        {a.verdict === 'objection'
          ? 'This model criticism is a proposal requiring its argument to be checked. A criticism of novelty or relevance does not invalidate the mathematical statement. The original claim remains available.'
          : a.verdict === 'insufficient-context'
            ? 'The reviewer could not justify a verdict from the supplied context. This is excluded from the pass count.'
            : 'Another provider found no specific objection in the supplied context. This does not establish the claim.'}
        {a.objectionNodeId && (
          <button type="button" onClick={() => useStore.getState().select(a.objectionNodeId!)}>
            Inspect proposed criticism
          </button>
        )}
        {!live &&
          ' This examined the wording before you rewrote it, so it no longer describes the claim above.'}
      </p>
    </div>
  )
}

function AuditButton({ node }: { node: ReasonNode }) {
  // Subscribed field by field. A bare `useStore()` here returned a snapshot the
  // component did not re-render on, so the button sat disabled after a pass
  // released the lock and only came back by being re-selected — a control that
  // is wrong until you poke it.
  //
  // The one press on this card that still greys out for any pass in flight
  // rather than waiting on the Up next line (D162). Its key is another house's,
  // resolved against whoever wrote the claim; its figure counts the branch it
  // would read; and whether it continues an unfinished batch is read off the
  // record at the press. The line re-judges a key role, one figure and a node —
  // a deferred cross-examination would be held to none of the terms its reader
  // saw, so it stays a press made when the lock is free.
  const busy = useStore((s) => s.busy)
  const modelId = useStore((s) => s.modelId)
  const roleModels = useStore((s) => s.roleModels)
  const keys = useStore((s) => s.keys)
  const customBase = useStore((s) => s.customBase)
  const customModel = useStore((s) => s.customModel)
  // Its figure includes the voice's line while the voice is on (D295).
  useVoiceOn()
  useFigureBasis()
  const a = node.audit
  const session = useStore((s) => s.session)
  // The house `runAudit` itself resolves (`auditorFor`: the branch's author's
  // other one), so the figure, the name and the pass read one model — the
  // proposal chips and the run gate's read it the same way.
  const auditor = auditorFor({ modelId, keys, customBase, customModel, session }, node.id)
  // The last batch that is not the one running. The one in flight wrote its
  // coverage before its first request, every row still `incomplete`, so reading
  // it as "the previous batch" printed "0/5" under "Continue unfinished
  // reviews" while the reviews were running — a failure that had not happened.
  const last = lastSettledAudit(session, node.id, busy)
  // `runAudit`'s continuation takes every row not `completed` — a review that
  // lacked context is asked again too — so the offer reads the same predicate.
  const unfinished = last?.auditCoverage?.claims.some((c) => c.research !== 'completed')
  const reviewCount = auditTargets(session, node.id).length
  const word = unfinished
    ? 'Continue unfinished reviews in a new pass'
    : a
      ? 'Cross-examine again'
      : 'Cross-examine from another provider'
  // Named as the other priced presses are (`pressName`, D217): the word, then the
  // one figure this press adds — a review per claim, as `VerbPrice` counts it,
  // and the voice's one line after the press on the Asking role's route (D295) —
  // or the status word standing where there is none.
  const est = auditor
    ? projectVerbUsd({ modelId, roleModels }, 'audit', auditor.entry, {
        count: Math.max(1, reviewCount),
      })
    : null
  const note = useId()

  return (
    <>
      <button
        className="action action--wide"
        style={{ '--act-tint': 'var(--sem-tension)' } as CSSProperties}
        disabled={node.parentId === null || !auditor}
        // Shut by the lock without leaving the tab order: `disabled` under the
        // pass its own press had just started took the button out from under the
        // key and dropped focus on the page body. Still focusable, it reads as
        // unavailable, and a press says why — `runAudit` refuses under the lock
        // out loud.
        aria-disabled={busy || undefined}
        aria-label={pressName(word, pressTail({ ready: auditor !== null, est }))}
        aria-describedby={note}
        title={
          auditor
            ? `Hands this branch to ${auditor.entry.name} and asks it to break the argument`
            : 'Needs a key for a second provider — an audit run by the same company is the panel again'
        }
        onClick={() => void runAudit(node.id, unfinished ? { continuationOf: last!.id } : {})}
      >
        <b style={{ width: '100%' }}>
          <Icon name="challenge" size={13} />
          {word}
          <VerbPrice verb="audit" auditor={auditor?.entry ?? null} count={reviewCount} />
        </b>
        <span id={note}>
          {auditor
            ? `Reviews up to ${claimCount(reviewCount)}, ${reviewCount === 1 ? 'in a request' : 'each in its own request'} to ${auditor.entry.name}, carrying definitions and evidence references. Each review identifies its scope or names missing context. Proposed criticisms remain unchecked.`
            : 'A panel is three passes of one model, so its agreement is one model agreeing with itself. This is the way out, and it needs a key for a company other than the one that wrote this claim.'}
        </span>
      </button>
    </>
  )
}

/**
 * What the last settled cross-examination batch came to — a record, so the
 * card's, while the press that would run another is the composer's Investigate
 * (D347). Read off the same settled batch the press reads (`lastSettledAudit`),
 * so the two cannot describe different batches.
 */
export function AuditRecord({ node }: { node: ReasonNode }) {
  const busy = useStore((s) => s.busy)
  const session = useStore((s) => s.session)
  const last = lastSettledAudit(session, node.id, busy)
  const batch = last ? auditBatch(session, last) : null
  if (!last) return null
  return (
    <>
      {/* What the last settled batch came to, counted — the report's own
          sentence (`auditTallySentence`), here on the card rather than only in
          a download. */}
      {batch && auditBatchSentence(batch) && (
        <p className="gnote" data-audit-tally>
          {auditBatchSentence(batch)}
        </p>
      )}
      {last?.auditCoverage && (
        <details className="gnote">
          <summary>Inspect the previous review's coverage</summary>
          {last.auditCoverage.claims.map((claim) => (
            <p key={claim.nodeId}>
              <b>{session.nodes[claim.nodeId]?.label ?? claim.nodeId}</b>: request {claim.transport}
              ; response {truncationWord(claim.truncated)}; structured answer {claim.structured};
              research {claim.research}.
            </p>
          ))}
          <p>
            {last.auditCoverage.skipped.length} claims outside this batch. These records are
            read-only; a continuation is a new paid request.
          </p>
        </details>
      )}
    </>
  )
}

/* ── the recorded result ──────────────────────────────────────────────── */

const BEARING_TINT: Record<string, string> = {
  supported: 'var(--sem-signal)',
  refuted: 'var(--sem-refuted)',
  inconclusive: 'var(--sem-weak)',
}

/**
 * Bring a node's outcome form onto the screen, after a press that opened it.
 *
 * Lives here because the attribute it looks for is written eight lines below,
 * and a caller that carried its own copy of the selector would go quietly blind
 * the day that attribute was renamed — which is the failure mode, not a crash.
 * `pokeRecord` opens the form; this is the other half, and every caller of the
 * one wants the other.
 *
 * A frame later at least, because the form is opened by a store write and
 * there is nothing to scroll to until React has painted it — and up to
 * `SEAT_FRAMES` later, because the block opens in an EFFECT reacting to that
 * write, whose render can land a frame or two behind the poke's on a busy
 * main thread. That used not to matter: the block always held a heading and a
 * sentence, so there was a box to centre even before the form arrived. The
 * card leaves an unrecorded, unopened block empty now (its status line and
 * Record link say what the sentence said), and an empty block has no box, so
 * this waits for the form rather than centring nothing.
 */
export function scrollToOutcomeForm(nodeId: string): void {
  seatWhenFilled(
    () =>
      [...document.querySelectorAll<HTMLElement>('[data-outcome-node]')].find(
        (el) => el.dataset.outcomeNode === nodeId,
      ),
    (block) => block.childElementCount > 0,
  )
}

/**
 * Bring a node's check record — the reading's stance strip, note, summary,
 * sources with their quotes and provenance — onto the screen (D335).
 *
 * The status line's reading and a check's message in the feed say what a check
 * found; the record that stands behind the sentence is the last block on the
 * card, below the folds, and nothing connected the two. Here beside
 * `scrollToOutcomeForm` for that function's reason: the attribute it looks for
 * is written in this file. Seated at the TOP rather than the centre, because a
 * record is usually taller than the viewport and centring it lands the reader
 * mid-list with the reading above them.
 */
export function scrollToCheckRecord(nodeId: string): void {
  seatWhenFilled(
    () =>
      [...document.querySelectorAll<HTMLElement>('[data-check-node]')].find(
        (el) => el.dataset.checkNode === nodeId,
      ),
    (record) => record.offsetParent !== null,
    'start',
  )
}

/** How many frames a form's scroll waits for the form, before scrolling to
 *  whatever is there. Six is a tenth of a second — past any render a poke can
 *  schedule, and short enough that a reader never sees the page catch up. */
const SEAT_FRAMES = 6

/**
 * Centre an element once it is ready, a frame at a time up to `SEAT_FRAMES`.
 * Shared with the card's rewrite editor (`scrollToRewriteEditor`), whose
 * editor opens by the same kind of effect, and with the check record, which
 * seats at the top instead (`block`).
 */
export function seatWhenFilled(
  find: () => HTMLElement | undefined,
  ready: (el: HTMLElement) => boolean,
  block: ScrollLogicalPosition = 'center',
): void {
  let frames = 0
  const seat = () => {
    const el = find()
    if ((!el || !ready(el)) && ++frames < SEAT_FRAMES) {
      requestAnimationFrame(seat)
      return
    }
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    el?.scrollIntoView({ block, behavior: calm ? 'auto' : 'smooth' })
  }
  requestAnimationFrame(seat)
}

const capFirst = (t: string): string => t.charAt(0).toUpperCase() + t.slice(1)

export function OutcomeBlock({ session, node }: { session: Session; node: ReasonNode }) {
  const busy = useStore((s) => s.busy)
  const sigRecord = useStore((s) => s.sigRecord)
  const [open, setOpen] = useState(false)
  const o = node.outcome
  const demo = session.id === DEMO_SESSION_ID
  // The worked example's own result, as it was written — not one a reader
  // recorded on it. Recording is free and keeps the demo's id, so `demo` alone
  // cannot tell them apart, and "Nobody ran this experiment" printed under the
  // reader's own observation was the card contradicting the person who made it.
  const authored = demo && isAuthoredDemoOutcome(node.id, o)

  useEffect(() => setOpen(false), [node.id])

  // The R key and the node card's FREE row arrive as one one-shot signal.
  // Reacting to the CHANGE, not the value, so a poke from before this block
  // mounted does not open a form nobody asked for.
  const seenRecord = useRef(sigRecord)
  useEffect(() => {
    if (sigRecord === seenRecord.current) return
    seenRecord.current = sigRecord
    setOpen(true)
  }, [sigRecord])

  const moved = o ? Math.abs(o.confidenceBefore - node.confidence) > 0.005 : false

  // Where this result stands in the ledger, read off the ledger itself — the
  // shelf plus this exploration, live — so the count here and the row the press
  // opens are one number (`resultPointer`). Keyed on what the pointer reads and
  // NOT on the session object, which changes on every streamed token: this parses
  // every stored exploration carrying a result, which is a press's worth of work
  // and not a frame's.
  const pointer = useMemo(
    () => (o ? resultPointer(calibrate(loadOutcomeRecords(session)), node, demo) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session.id, o?.recordedAt, o?.bearing, node.model?.id, node.origin, node.editedAt, demo],
  )
  const pokeLedger = useStore((s) => s.pokeLedger)

  return (
    <div className="group" data-outcome-node={node.id} style={{ gap: 'var(--s2)' }}>
      {/* The heading only over something to head. With nothing recorded and
          the form shut this block is an empty anchor — the card's status line
          already says nothing has been observed ("not checked yet"), and its
          Record link is the door; a third copy here was a banner saying so. */}
      {(o || open) && <span className="eyebrow">From the world</span>}

      {o && (
        <div className="outcome">
          <div
            className="grow-chip"
            style={{ '--c-tint': BEARING_TINT[o.bearing] } as CSSProperties}
          >
            <i />
            <b>{BEARING_LABEL[o.bearing]}</b>
            <span>observed {fmtDay(o.at)}</span>
          </div>
          <blockquote>{o.observed}</blockquote>
          {o.source && <span className="srcs-meta">{o.source}</span>}
          <p className="gnote">
            {moved
              ? `You moved this node's confidence from ${o.confidenceBefore.toFixed(2)} to ${node.confidence.toFixed(2)}. The first number was the model's.`
              : `Confidence unchanged at ${node.confidence.toFixed(2)} — the tool suggests no number for this.`}
          </p>
          {authored && (
            <p className="gnote gnote--quiet">
              Part of the demo exploration. Nobody ran this experiment — the result was written by
              hand to show what a recorded one looks like.
            </p>
          )}
          {/* After a result, the score it counts toward — the one table in the
              app that grades the model, which nothing here used to point at. The
              authored note above already says why the demo's own result counts for
              none; a result a reader records on the demo gets this line, which says
              it is part of the worked example and scores no model. */}
          {pointer && !authored && (
            <p className="gnote gnote--quiet">
              {capFirst(resultPointerLine(pointer))}
              {pointer.kind !== 'none' && (
                <>
                  {' · '}
                  <button className="linkish" onClick={pokeLedger}>
                    See the ledger
                  </button>
                </>
              )}
            </p>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button
              className="btn btn--outline"
              disabled={busy}
              onClick={() => void rederiveFromOutcome(node.id)}
              title="Drops everything below this node and reasons it again from the result"
            >
              <Icon name="spark" size={13} />
              Re-derive from this
            </button>
            <button className="btn" disabled={busy} onClick={() => setOpen(true)}>
              <Icon name="edit" size={13} />
              Edit
            </button>
            <button
              className="btn"
              disabled={busy}
              style={{ color: 'var(--text-3)' }}
              onClick={() => withdrawOutcome(node.id)}
            >
              Withdraw
            </button>
          </div>
        </div>
      )}

      {/* No trigger button of its own: the node card's Record link and the R
          key open the sheet through `sigRecord`, so a second identical
          affordance here would just be the same door twice. The sentence this
          block used to print when empty — nothing observed yet, and a result
          outranks the model — rides that link now, where the door is. */}
      {open && <OutcomeSheet node={node} onClose={() => setOpen(false)} />}
    </div>
  )
}

/**
 * The writing surface for a result.
 *
 * The copy here matters more than anywhere else in the app, because this is the
 * one place a user contributes a fact rather than reading one. Three things it
 * refuses to do: paraphrase what they wrote, suggest a confidence, or make
 * recording and re-deriving the same button.
 */
function OutcomeSheet({ node, onClose }: { node: ReasonNode; onClose: () => void }) {
  const busy = useStore((s) => s.busy)
  // Named apart from the `ready` below, which asks whether the user has written
  // enough to record. Recording never needs a key; re-deriving does — reasoning's,
  // a routed one where routed (`routeReady`), as its pass asks.
  const canRun = useRouteReady('revise')
  const existing = node.outcome
  const [observed, setObserved] = useState(existing?.observed ?? '')
  // Nothing chosen until the person chooses. The form opened on "Did not settle
  // it" already pressed, so a result recorded without a second look went down as
  // inconclusive — an answer the person never gave, on the one form whose whole
  // claim is that what lands is theirs. An edit keeps the bearing it was saved with.
  const [bearing, setBearing] = useState<'supported' | 'refuted' | 'inconclusive' | null>(
    existing?.bearing ?? null,
  )
  // The reader's calendar day, as the field hands one back. It was the UTC day,
  // so east of UTC before breakfast the form opened on yesterday and would not
  // offer today at all, and west of it in the evening it offered tomorrow.
  const [day, setDay] = useState(fmtDay(existing?.at ?? Date.now()))
  const [source, setSource] = useState(existing?.source ?? '')
  const [confidence, setConfidence] = useState(node.confidence)

  const ready = observed.trim().length >= 8 && bearing !== null
  // Why Record is still off, said on the button rather than left to be guessed.
  const notYet =
    observed.trim().length < 8
      ? 'Write down what you observed first — a sentence is enough.'
      : bearing === null
        ? 'Say how it bears on the claim first.'
        : undefined

  const submit = (rederive: boolean) => {
    if (bearing === null) return
    // Parsed as that day's local noon rather than UTC: a result observed "on the
    // 12th" was observed on the 12th where the user was.
    const at = dayInputNoon(day) ?? existing?.at ?? Date.now()
    onClose()
    void recordOutcome(node.id, { observed, bearing, at, source, confidence }, { rederive })
  }

  return (
    <div className="editbox">
      <p className="editbox-note">
        What did you actually observe? Write it as you would in a notebook. It goes to the model
        verbatim — nothing here is paraphrased or tidied, because a tidied result is a stronger
        result than the one you got.
      </p>
      <textarea
        className="textarea"
        rows={5}
        value={observed}
        onChange={(e) => setObserved(e.target.value)}
        // Field-neutral, with no example at all: the first was a cryogenic force
        // measurement and the second still spoke of a noise floor and a trial arm,
        // and a historian or a policy analyst recording a result had neither.
        placeholder="What you ran, read, asked or saw, and what came of it — in your own words, with any numbers or dates that matter."
        aria-label="What you observed"
      />

      <div className="field">
        <span className="eyebrow">How it bears on this claim</span>
        <div className="tabs" role="tablist" aria-label="How the result bears on the claim">
          {(['supported', 'refuted', 'inconclusive'] as const).map((b) => (
            <button key={b} role="tab" aria-selected={bearing === b} onClick={() => setBearing(b)}>
              {b === 'supported'
                ? 'Went its way'
                : b === 'refuted'
                  ? 'Went against it'
                  : 'Did not settle it'}
            </button>
          ))}
        </div>
        <p className="gnote gnote--quiet">
          Whether the observation went the claim's way — not whether the claim is true.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <label className="field" style={{ flex: '0 0 auto' }}>
          <span className="eyebrow">Observed on</span>
          <input
            className="input"
            type="date"
            value={day}
            max={fmtDay(Date.now())}
            onChange={(e) => setDay(e.target.value)}
          />
        </label>
        <label className="field" style={{ flex: 1 }}>
          <span className="eyebrow">Where it came from</span>
          <input
            className="input"
            value={source}
            placeholder="a notebook, dataset, report, interview or link"
            onChange={(e) => setSource(e.target.value)}
          />
        </label>
      </div>

      <div className="field">
        <div className="field-head">
          <span className="eyebrow">Confidence in the claim now</span>
          <span className="field-meta num">{confidence.toFixed(2)}</span>
        </div>
        <Range
          value={confidence}
          onChange={setConfidence}
          aria-label="Confidence in the claim now"
        />
        <p className="gnote gnote--quiet">
          The model put this at {node.confidence.toFixed(2)}. Move it if you want to; the tool will
          not move it for you, and leaving it alone is recorded as unchanged.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          className="btn btn--primary"
          disabled={!ready || busy}
          title={notYet}
          onClick={() => submit(false)}
        >
          <Icon name="check" size={13} />
          Record it
        </button>
        <button
          className="btn btn--outline"
          disabled={!ready || busy}
          onClick={() => submit(true)}
          title={
            notYet ??
            (canRun
              ? 'Records the result, then drops everything below this node and reasons it again'
              : 'Recording works without a key. Re-deriving needs one.')
          }
        >
          <Icon name="spark" size={13} />
          Record and re-derive
          {/* Priced like the editor's Re-derive: the second half of this press is
              a reasoning pass, and a paid press with no figure was the last one
              in the app. Recording alone stays free and unpriced beside it. */}
          <VerbPrice verb="move" />
        </button>
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
      </div>
      <p className="gnote gnote--quiet">
        Re-deriving discards the reasoning below this node and pays for a new pass. ⌘Z puts it back.
      </p>
    </div>
  )
}
